import React, { useState, useEffect, useCallback } from "react";
import { nanoid } from "nanoid";
import { useParams, useNavigate } from "react-router-dom";
import { generateChordSlug } from "./CreateEvent/utils";
import { apiClient } from "../../utils/api";
import { toast } from "react-toastify";
import { useVenueHistory } from "../../hooks/useVenueHistory";
import type { VenueData } from "../../types/venue";

import BasicInfo from "./CreateEvent/BasicInfo";
import VenueInfo from "./CreateEvent/VenueInfo";
import ImageUpload from "./CreateEvent/ImageUpload";
import DateTimePicker from "./CreateEvent/DateTimePicker";
import OpeningTimes from "./CreateEvent/OpeningTimes";
import TicketTypes from "./CreateEvent/TicketTypes";
import EventSettings from "./CreateEvent/EventSettings";
import BackgroundImageUpload, {
	BackgroundImageData,
} from "./CreateEvent/BackgroundImageUpload";
import EventCautions, {
	CautionItem,
} from "./CreateEvent/EventCautions";
import HashtagInput from "./HashtagInput";
import TicketSyncSettings from "./TicketSyncSettings";
import { useAuth } from "../../contexts/AuthContext";

/**
 * 開場・開演時間
 */
type OpeningTime = {
	/** フロントエンド用の一時ID */
	id: string;
	/** バックエンドで管理されるexternal_id */
	external_id?: string;
	openTime: string;
	startTime: string;
	endTime: string;
	description?: string;
};

/**
 * チケットタイプ
 */
type TicketType = {
	/** フロントエンド用の一時ID */
	id: string;
	/** バックエンドで管理されるexternal_id */
	external_id?: string;
	name: string;
	description: string;
	isFree: boolean;
	price: number;
	hasLimit: boolean;
	quantity: number;
	publicationDateTime: string;
	saleStartDateTime: string;
	saleEndDateTime: string;
	/** OpeningTime の外部IDリスト */
	openingTimeIds: string[];
	soldTickets: number;
	syncPartnerTicketId?: string;
	syncPartnerTicketTitle?: string;
};

/**
 * 画像データ
 */
type ImageType = {
	id: string;
	file: File | null;
	previewUrl: string;
	order: number;
	objectName?: string;
	publicUrl?: string;
	uploading?: boolean;
	error?: string;
};

type FormDataType = {
	title: string;
	slug: string;
	description: string;
	date: string;
	venue: VenueData;
	images: ImageType[];
	backgroundImage: BackgroundImageData | null;
	openingTimes: OpeningTime[];
	ticketTypes: TicketType[];
	publicationDateTime: string;
	saleStartDateTime: string;
	saleEndDateTime: string;
	hashtags: string[];
	status: "draft" | "published";
	cautions: CautionItem[];
	ticketSyncEnabled: boolean;
	ticketSyncPartnerEventId: string;
	ticketSyncPartnerEventTitle: string;
};

const TICKET_SYNC_ALLOWED_EMAIL = "admin@miyatako-hei.com";

const EditEvent: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user } = useAuth();
	const canManageTicketSync =
		user?.email?.toLowerCase() === TICKET_SYNC_ALLOWED_EMAIL;

	const [formData, setFormData] = useState<FormDataType>({
		title: "",
		slug: "",
		description: "",
		date: "",
		venue: {
			venueName: "",
			postalCode: "",
			prefecture: "",
			city: "",
			address: "",
		},
		images: [],
		backgroundImage: null,
		openingTimes: [],
		ticketTypes: [],
		publicationDateTime: "",
		saleStartDateTime: "",
		saleEndDateTime: "",
		hashtags: [],
		status: "draft",
		cautions: [{ id: nanoid(8), content: "" }],
		ticketSyncEnabled: false,
		ticketSyncPartnerEventId: "",
		ticketSyncPartnerEventTitle: "",
	});

	const { venueHistory, addVenueToHistory } = useVenueHistory();

	const [isLoading, setIsLoading] = useState(true);
	const [isEditingSlug, setIsEditingSlug] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [slugCheckStatus, setSlugCheckStatus] = useState<
		"idle" | "checking" | "available" | "taken" | "error"
	>("idle");
	const [slugCheckMessage, setSlugCheckMessage] = useState<string | null>(null);
	const [eventExternalId, setEventExternalId] = useState<string | null>(null);
	const isSlugCompletionDisabled = isEditingSlug && slugCheckStatus === "taken";

	const applyEventDataToForm = useCallback((eventData: any) => {
		const formattedOpeningTimes = eventData.opening_times.map((ot: any) => {
			const frontendId = nanoid(8);
			return {
				id: frontendId,
				external_id: ot.external_id,
				openTime: ot.open_time,
				startTime: ot.start_time,
				endTime: ot.end_time,
				description: ot.description || "",
			};
		});

		const formattedTicketTypes = eventData.ticket_types.map((tt: any) => {
			const openingTimeIds = tt.opening_times
				.map((ot: any) => ot.external_id)
				.filter(Boolean);

			return {
				id: nanoid(8),
				external_id: tt.external_id,
				name: tt.name,
				description: tt.description || "",
				isFree: tt.is_free,
				price: Math.round(tt.price),
				hasLimit: tt.has_limit,
				quantity: tt.quantity || 100,
				publicationDateTime: tt.publication_date_time || "",
				saleStartDateTime: tt.sale_start_date_time || "",
				saleEndDateTime: tt.sale_end_date_time || "",
				openingTimeIds,
				soldTickets: tt.sold_tickets ?? 0,
				syncPartnerTicketId: tt.ticket_sync_partner_ticket_id
					? String(tt.ticket_sync_partner_ticket_id)
					: "",
				syncPartnerTicketTitle: tt.ticket_sync_partner_ticket_title || "",
			};
		});

		const formattedImages = eventData.event_images
			? eventData.event_images.map((img: any) => ({
					id: nanoid(8),
					file: null,
					previewUrl: img.public_url,
					order: img.order || 0,
					objectName: img.object_name,
					publicUrl: img.public_url,
			  }))
			: [];

		const venuePayload: VenueData = {
			venueName: eventData.venue_name || "",
			postalCode: eventData.postal_code || "",
			prefecture: eventData.prefecture || "",
			city: eventData.city || "",
			address: eventData.address || "",
		};

		setFormData({
			title: eventData.title,
			slug: eventData.slug,
			description: eventData.description || "",
			date: eventData.date,
			venue: venuePayload,
			images: formattedImages,
			backgroundImage: eventData.background_image_url
				? {
						previewUrl: eventData.background_image_url,
						publicUrl: eventData.background_image_url,
				  }
				: null,
			openingTimes: formattedOpeningTimes,
			ticketTypes: formattedTicketTypes,
			publicationDateTime: eventData.publication_date_time || "",
			saleStartDateTime: eventData.sale_start_date_time || "",
			saleEndDateTime: eventData.sale_end_date_time || "",
			hashtags: eventData.hashtags || [],
			status: eventData.status,
			cautions:
				eventData.cautions && eventData.cautions.length > 0
					? eventData.cautions
							.sort(
								(a: any, b: any) =>
									(a.position ?? 0) - (b.position ?? 0)
							)
							.map((caution: any) => ({
								id: String(caution.id ?? nanoid(6)),
								content: caution.content || "",
							}))
					: [{ id: nanoid(6), content: "" }],
			ticketSyncEnabled: Boolean(eventData.ticket_sync_enabled),
			ticketSyncPartnerEventId: eventData.ticket_sync_partner_event_id
				? String(eventData.ticket_sync_partner_event_id)
				: "",
			ticketSyncPartnerEventTitle:
				eventData.ticket_sync_partner_event_title || "",
		});
		addVenueToHistory(venuePayload);
		setEventExternalId(eventData.external_id);
	}, [addVenueToHistory]);

	useEffect(() => {
		const fetchEventData = async () => {
			try {
				const response = await apiClient.get(`/organizer/events/${id}`);
				applyEventDataToForm(response.data.event);

				setIsLoading(false);
			} catch (error: any) {
				console.error("イベントデータの取得に失敗しました:", error);
				toast.error("イベントデータの取得に失敗しました");
				setIsLoading(false);
				navigate("/organizer/events");
			}
		};

		fetchEventData();
	}, [id, navigate, applyEventDataToForm]);

	// --------------------
	// イベント名・スラッグ
	// --------------------
	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

const toggleSlugEditMode = () => {
	if (isEditingSlug && slugCheckStatus === "taken") {
		return;
	}
	setIsEditingSlug((prev) => {
		const next = !prev;
		if (!next) {
			setSlugCheckStatus("idle");
			setSlugCheckMessage(null);
		}
		return next;
	});
};

const regenerateSlug = () => {
	setFormData((prev) => ({
		...prev,
		slug: generateChordSlug(),
	}));
};

useEffect(() => {
	if (!isEditingSlug || !formData.slug) {
		setSlugCheckStatus("idle");
		setSlugCheckMessage(null);
		return;
	}

	let isActive = true;
	setSlugCheckStatus("checking");
	setSlugCheckMessage("スラッグを確認しています...");

	const debounceId = setTimeout(async () => {
		try {
			const response = await apiClient.get(
				"/organizer/events/slug/check",
				{
					params: {
						slug: formData.slug,
						exclude_external_id: eventExternalId ?? undefined,
					},
				}
			);

			if (!isActive) return;

			if (response.data.available) {
				setSlugCheckStatus("available");
				setSlugCheckMessage("このスラッグは使用できます");
			} else {
				setSlugCheckStatus("taken");
				setSlugCheckMessage("このスラッグは既に使用されています");
			}
		} catch (error) {
			console.error("Failed to validate slug", error);
			if (!isActive) return;
			setSlugCheckStatus("error");
			setSlugCheckMessage("スラッグの確認に失敗しました");
		}
	}, 500);

	return () => {
		isActive = false;
		clearTimeout(debounceId);
	};
}, [formData.slug, isEditingSlug, eventExternalId]);

	const formatEventDateLabel = () => {
		if (!formData.date) return "";
		const eventDateObj = new Date(formData.date);
		if (Number.isNaN(eventDateObj.getTime())) return "";
		return `${eventDateObj.getFullYear()}年${eventDateObj.getMonth() + 1}月${eventDateObj.getDate()}日`;
	};

	const isAfterEventDate = (value: string) => {
		if (!formData.date || !value) return false;
		const eventDateObj = new Date(formData.date);
		const targetDateObj = new Date(value);
		if (
			Number.isNaN(eventDateObj.getTime()) ||
			Number.isNaN(targetDateObj.getTime())
		) {
			return false;
		}
		const limit = new Date(eventDateObj);
		limit.setHours(23, 59, 59, 999);
		return targetDateObj.getTime() > limit.getTime();
	};

	// --------------------
	// 開場・開演時間
	// --------------------
	const handleOpeningTimeChange = (
		id: string,
		field: keyof OpeningTime,
		value: string
	) => {
		setFormData((prev) => ({
			...prev,
			openingTimes: prev.openingTimes.map((ot) =>
				ot.id === id ? { ...ot, [field]: value } : ot
			),
		}));
	};

	const handleStartTimeChange = (id: string, value: string) => {
		handleOpeningTimeChange(id, "startTime", value);
	};

	const addOpeningTime = () => {
		// 新規追加時は一時IDを生成して、external_idにも同じIDを設定する
		// バックエンドで新しい external_id が割り当てられるまでこの値が使われる
		const newId = nanoid(10);
		console.log("Adding new opening time with temporary external_id:", newId);

		setFormData((prev) => ({
			...prev,
			openingTimes: [
				...prev.openingTimes,
				{
					id: newId, // フロントエンド用一時ID
					external_id: newId, // フロントエンド用一時IDと同じ値を external_id にも設定
					openTime: "18:00",
					startTime: "19:00",
					endTime: "21:00",
					description: "",
				},
			],
		}));
	};

	const removeOpeningTime = (removeId: string) => {
		// 削除する開場時間を探す
		const openingTimeToRemove = formData.openingTimes.find(
			(ot) => ot.id === removeId
		);
		if (!openingTimeToRemove) return;

		// バックエンドの external_id があればそれを使う、なければフロントエンドの一時 ID を使う
		const idToFilter = openingTimeToRemove.external_id || removeId;

		console.log("Removing opening time:", {
			frontendId: removeId,
			externalId: openingTimeToRemove.external_id,
			idToFilter,
		});

		setFormData((prev) => ({
			...prev,
			// フロントエンドの一時IDでフィルタリング（コンポーネント表示用）
			openingTimes: prev.openingTimes.filter((t) => t.id !== removeId),
			ticketTypes: prev.ticketTypes.map((ticket) => ({
				...ticket,
				// チケットと紐づいている opening_time の参照は external_id を基準にフィルタリング
				// これにより、バックエンドでの参照整合性が保たれる
				openingTimeIds: ticket.openingTimeIds.filter(
					(tid) => tid !== idToFilter
				),
			})),
		}));
	};

	// --------------------
	// チケット
	// --------------------
	const handleTicketTypeChange = (
		id: string,
		field: keyof TicketType,
		value: any
	) => {
		const targetTicket = formData.ticketTypes.find((ticket) => ticket.id === id);
		if (!targetTicket) return;
		let nextValue = value;
		const hasPurchases = targetTicket.soldTickets > 0;
		const lockedFields: (keyof TicketType)[] = [
			"isFree",
			"price",
			"publicationDateTime",
			"saleStartDateTime",
		];

		if (field === "price" && typeof value === "number") {
			nextValue = Math.round(value);
		}

		if (hasPurchases && lockedFields.includes(field)) {
			toast.info("購入済みのチケットはこの項目を変更できません");
			return;
		}

		if (field === "quantity") {
			const numericValue =
				typeof nextValue === "number"
					? nextValue
					: parseInt(String(nextValue), 10);
			if (
				hasPurchases &&
				Number.isFinite(numericValue) &&
				numericValue < targetTicket.soldTickets
			) {
				toast.error(
					`販売済み ${targetTicket.soldTickets} 枚を下回る数には変更できません`
				);
				return;
			}
		}

		if (field === "saleEndDateTime" && typeof nextValue === "string" && nextValue) {
			if (isAfterEventDate(nextValue)) {
				const dateLabel = formatEventDateLabel();
				toast.error(
					dateLabel
						? `販売終了日時はイベント開催日（${dateLabel}）までに設定してください`
						: "販売終了日時はイベント開催日を超えて設定できません"
				);
				return;
			}
		}

		if (field === "hasLimit" && nextValue === true) {
			setFormData((prev) => ({
				...prev,
				ticketTypes: prev.ticketTypes.map((ticket) =>
					ticket.id === id
						? {
							...ticket,
							hasLimit: true,
							quantity: Math.max(ticket.quantity, ticket.soldTickets || 1),
						}
						: ticket
				),
			}));
			return;
		}

		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.map((ticket) =>
				ticket.id === id ? { ...ticket, [field]: nextValue } : ticket
			),
		}));
	};

	const addTicketType = () => {
		setFormData((prev) => ({
			...prev,
			ticketTypes: [
				...prev.ticketTypes,
				{
					id: nanoid(8), // フロントエンド用一時ID
					external_id: undefined, // バックエンドで割り当てられる
					name: "",
					description: "",
					isFree: false,
					price: 1000,
					hasLimit: false,
					quantity: 100,
					publicationDateTime: "",
					saleStartDateTime: "",
					saleEndDateTime: "",
					// openingTimeIds には、OpeningTime の external_id が入る
					// 空の配列で初期化
					openingTimeIds: [],
					soldTickets: 0,
				},
			],
		}));
	};

	const removeTicketType = (removeId: string) => {
		const targetTicket = formData.ticketTypes.find((t) => t.id === removeId);
		if ((targetTicket?.soldTickets ?? 0) > 0) {
			toast.error("購入済みのチケットは削除できません");
			return;
		}
		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.filter((t) => t.id !== removeId),
		}));
	};

	const toggleOpeningTimeForTicket = (
		ticketId: string,
		openingTimeId: string
	) => {
		const targetTicket = formData.ticketTypes.find((ticket) => ticket.id === ticketId);
		if ((targetTicket?.soldTickets ?? 0) > 0) {
			toast.info("購入済みのチケットは対象の開場/開演情報を変更できません");
			return;
		}
		// 選択された開場時間を取得
		const selectedOpeningTime = formData.openingTimes.find(
			(ot) => ot.id === openingTimeId
		);
		if (!selectedOpeningTime) return;

		// external_id を優先的に使用する
		// バックエンドから取得した opening_time の external_id があれば、それを使用
		// なければフロントエンドの一時IDを使用
		const idToToggle = selectedOpeningTime.external_id || openingTimeId;

		console.log("Toggling opening time for ticket:", {
			ticketId,
			openingTimeId,
			externalId: selectedOpeningTime.external_id,
			idToToggle,
		});

		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.map((ticket) => {
				if (ticket.id === ticketId) {
					const openingTimeIds = [...ticket.openingTimeIds];
					const index = openingTimeIds.indexOf(idToToggle);
					if (index === -1) {
						openingTimeIds.push(idToToggle);
					} else {
						openingTimeIds.splice(index, 1);
					}
					return { ...ticket, openingTimeIds };
				}
				return ticket;
			}),
		}));
	};

	const handleTicketSyncEnabledChange = (enabled: boolean) => {
		setFormData((prev) => ({
			...prev,
			ticketSyncEnabled: enabled,
		}));
	};

	const handleTicketSyncPartnerEventIdChange = (value: string) => {
		setFormData((prev) => ({
			...prev,
			ticketSyncPartnerEventId: value,
		}));
	};

	const handleTicketSyncMappingChange = (ticketId: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			ticketTypes: prev.ticketTypes.map((ticket) =>
				ticket.id === ticketId
					? { ...ticket, syncPartnerTicketId: value }
					: ticket
			),
		}));
	};

	// --------------------
	// フォーム送信
	// --------------------
	const handleSubmit = async (
		e: React.FormEvent,
		status: "draft" | "published"
	) => {
		e.preventDefault();
		const updatedFormData = { ...formData, status };
		setIsSubmitting(true);

		if (
			updatedFormData.saleEndDateTime &&
			isAfterEventDate(updatedFormData.saleEndDateTime)
		) {
			const dateLabel = formatEventDateLabel();
			toast.error(
				dateLabel
					? `イベント全体の販売終了日時は ${dateLabel} までに設定してください`
					: "販売終了日時はイベント開催日を超えて設定できません"
			);
			setIsSubmitting(false);
			return;
		}

		const invalidTicket = updatedFormData.ticketTypes.find(
			(ticket) =>
				ticket.saleEndDateTime && isAfterEventDate(ticket.saleEndDateTime)
		);
		if (invalidTicket) {
			toast.error(
				`チケット「${invalidTicket.name || "名称未設定"}」の販売終了日時がイベント開催日を超えています`
			);
			setIsSubmitting(false);
			return;
		}

		try {
			const submitData = new FormData();

			if (updatedFormData.backgroundImage?.uploading) {
				toast.error("背景画像のアップロード完了をお待ちください");
				setIsSubmitting(false);
				return;
			}

			// 基本情報
			submitData.append("title", updatedFormData.title);
			submitData.append("slug", updatedFormData.slug);
			submitData.append("description", updatedFormData.description || "");
			submitData.append("date", updatedFormData.date);
			submitData.append("status", updatedFormData.status);

			// 会場情報
			submitData.append("venue_name", updatedFormData.venue.venueName);
			submitData.append("postal_code", updatedFormData.venue.postalCode);
			submitData.append("prefecture", updatedFormData.venue.prefecture);
			submitData.append("city", updatedFormData.venue.city);
			submitData.append("address", updatedFormData.venue.address);
			submitData.append(
				"hashtags",
				JSON.stringify(updatedFormData.hashtags.slice(0, 10))
			);

			// 開場・開演時間（JSON文字列）
			// external_id が既に存在する場合は、それを "id" フィールドとして送信し、バックエンドでの参照に使用
			// 新規追加の場合は、フロントエンド側の一時IDをそのまま送信
			const openingTimesPayload = updatedFormData.openingTimes.map((time) => ({
				id: time.external_id || time.id, // external_id を優先して送信（バックエンドの識別用）
				external_id: time.external_id, // バックエンドが持つ external_id を明示的に送信
				open_time: time.openTime,
				start_time: time.startTime,
				end_time: time.endTime,
				description: time.description || "",
			}));
			console.log("Opening Times Payload:", openingTimesPayload); // For debugging
			submitData.append("opening_times", JSON.stringify(openingTimesPayload));

			// チケット（JSON文字列）
			const ticketTypesPayload = updatedFormData.ticketTypes.map((ticket) => {
				const payload = {
					name: ticket.name,
					description: ticket.description || "",
					is_free: ticket.isFree,
					price: ticket.price,
					has_limit: ticket.hasLimit,
					quantity: ticket.quantity,
					publication_date_time: ticket.publicationDateTime || null,
					sale_start_date_time: ticket.saleStartDateTime || null,
					sale_end_date_time: ticket.saleEndDateTime || null,
					// external_id が既に存在する場合は送信（更新用）
					external_id: ticket.external_id,
					// openingTimeIds は必ず external_id による参照になるようにする
					// これらのIDはバックエンドで opening_times テーブルの external_id と紐付けられる
					opening_time_ids: ticket.openingTimeIds.filter(Boolean),
				};

				console.log(
					`Ticket "${ticket.name}" opening time IDs:`,
					ticket.openingTimeIds
				);

				return payload;
			});

			console.log("Ticket Types Payload:", ticketTypesPayload); // For debugging
			submitData.append("ticket_types", JSON.stringify(ticketTypesPayload));

			const cautionPayload = updatedFormData.cautions
				.map((item, index) => ({
					content: item.content.trim(),
					position: index,
				}))
				.filter((item) => item.content.length > 0);

			submitData.append("cautions", JSON.stringify(cautionPayload));

			if (canManageTicketSync) {
				submitData.append(
					"ticket_sync_config",
					JSON.stringify({
						enabled: updatedFormData.ticketSyncEnabled,
						partner_event_id: updatedFormData.ticketSyncPartnerEventId
							? Number(updatedFormData.ticketSyncPartnerEventId)
							: null,
						tickets: updatedFormData.ticketTypes.map((ticket) => ({
							id: ticket.id,
							external_id: ticket.external_id,
							partner_ticket_id: ticket.syncPartnerTicketId
								? Number(ticket.syncPartnerTicketId)
								: null,
						})),
					})
				);
			}

			// 公開設定
			if (updatedFormData.publicationDateTime) {
				submitData.append(
					"publication_date_time",
					updatedFormData.publicationDateTime
				);
			}
			if (updatedFormData.saleStartDateTime) {
				submitData.append(
					"sale_start_date_time",
					updatedFormData.saleStartDateTime
				);
			}
			if (updatedFormData.saleEndDateTime) {
				submitData.append(
					"sale_end_date_time",
					updatedFormData.saleEndDateTime
				);
			}

			if (updatedFormData.backgroundImage?.publicUrl) {
				submitData.append(
					"background_image_url",
					updatedFormData.backgroundImage.publicUrl
				);
			} else if (updatedFormData.backgroundImage === null) {
				submitData.append("background_image_url", "");
			}

			// 画像関連
			const existingImagesData = updatedFormData.images
				.filter((img) => img.publicUrl && img.objectName)
				.map((img) => ({
					public_url: img.publicUrl,
					object_name: img.objectName,
					order: img.order,
				}));
			if (existingImagesData.length > 0) {
				submitData.append(
					"existing_images",
					JSON.stringify(existingImagesData)
				);
			}

			const newImages = updatedFormData.images.filter(
				(img) => img.file && !img.publicUrl
			);
			newImages.forEach((img, index) => {
				if (img.file) {
					submitData.append(`images[${index}]`, img.file);
					submitData.append(`imageOrders[${index}]`, String(img.order));
				}
			});

			console.log("==== 送信データ(FormData) ====");
			submitData.forEach((value, key) => {
				if (value instanceof File) {
					console.log(`${key} (File): ${value.name}`);
				} else {
					console.log(`${key}: ${value}`);
				}
			});

			// 画像データのログ出力 (デバッグ用)
			if (existingImagesData.length > 0) {
				console.log(
					"送信する画像データ:",
					JSON.stringify(existingImagesData, null, 2)
				);
			}

			// PUT メソッドを使うときは query で _method=PUT を付ける
			// multipart/form-data の場合は Content-Type ヘッダーを手動で設定しない
			// axios が自動的に境界値を設定する
			const response = await apiClient.post(
				`/organizer/events/${id}?_method=PUT`,
				submitData
			);

			console.log("API 応答:", response.data);
			if (response.data?.event) {
				applyEventDataToForm(response.data.event);
			}

			toast.success(
				status === "published"
					? "イベントが更新され公開されました！"
					: "イベントが更新され下書き保存されました！"
			);

			addVenueToHistory(updatedFormData.venue);
		} catch (err: any) {
			console.error("APIエラーの詳細:", err.response?.data || err);
			const validationErrors = err.response?.data?.errors;
			const firstValidationMessage = validationErrors
				? Object.values(validationErrors).flat()[0]
				: null;
			const errorMessage =
				firstValidationMessage ||
				err.response?.data?.message ||
				"更新に失敗しました";
			toast.error(String(errorMessage));
		} finally {
			setIsSubmitting(false);
		}
	};

	if (isLoading) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto"></div>
					<p className="mt-4 text-gray-600 dark:text-gray-400">
						イベント情報を読み込み中...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="mb-8">
				<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
					イベント編集
				</h2>
				<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
					イベントの詳細情報を編集してください
				</p>
			</div>
			<form onSubmit={(e) => handleSubmit(e, formData.status)}>
				<div className="space-y-6">
					{/* イベント基本情報 */}
				<BasicInfo
					formData={{
						title: formData.title,
						slug: formData.slug,
						description: formData.description,
					}}
					handleInputChange={handleInputChange}
					isEditingSlug={isEditingSlug}
					toggleSlugEditMode={toggleSlugEditMode}
					regenerateSlug={regenerateSlug}
					slugCheckStatus={slugCheckStatus}
					slugCheckMessage={slugCheckMessage}
					slugCompletionDisabled={isSlugCompletionDisabled}
				/>

					{/* 開催日 */}
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
							開催日
						</h3>
						<DateTimePicker
							id="event-date"
							label="開催日"
							value={formData.date}
							onChange={(value: string) =>
								setFormData((prev) => ({ ...prev, date: value }))
							}
							required
							placeholder="開催日を選択してください"
							dateOnly
						/>
					</div>

					{/* 会場情報 */}
					<VenueInfo
						venueData={formData.venue}
						onChange={(venueData) =>
							setFormData((prev) => ({
								...prev,
								venue: venueData,
							}))
						}
						required={true}
						venueHistory={venueHistory}
						showHistorySelector={false}
					/>

					{/* 画像アップロード */}
					<ImageUpload
						formData={{ images: formData.images }}
						setFormData={setFormData}
					/>

					{/* 背景画像アップロード */}
					<BackgroundImageUpload
						value={formData.backgroundImage}
						onChange={(image) =>
							setFormData((prev) => ({
								...prev,
								backgroundImage: image,
							}))
						}
					/>

					{/* 開場・開演時間 */}
					<OpeningTimes
						openingTimes={formData.openingTimes}
						handleOpeningTimeChange={handleOpeningTimeChange}
						addOpeningTime={addOpeningTime}
						removeOpeningTime={removeOpeningTime}
						handleStartTimeChange={handleStartTimeChange}
					/>

					{/* チケットタイプ */}
					<TicketTypes
						ticketTypes={formData.ticketTypes}
						openingTimes={formData.openingTimes}
						handleTicketTypeChange={handleTicketTypeChange}
						addTicketType={addTicketType}
						removeTicketType={removeTicketType}
						toggleOpeningTimeForTicket={toggleOpeningTimeForTicket}
					/>

					<EventCautions
						items={formData.cautions}
						onChange={(next) =>
							setFormData((prev) => ({
								...prev,
								cautions: next,
							}))
						}
					/>

					{/* ハッシュタグ */}
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
						<HashtagInput
							value={formData.hashtags}
							onChange={(hashtags) =>
								setFormData((prev) => ({ ...prev, hashtags }))
							}
							max={10}
							description="イベント検索やSNSで使うキーワードを最大10個まで設定できます（Enter／カンマで追加）"
							placeholder="例）ライブ, ジャズ, 東京"
						/>
					</div>

					{canManageTicketSync ? (
						<TicketSyncSettings
							enabled={formData.ticketSyncEnabled}
							partnerEventId={formData.ticketSyncPartnerEventId}
							partnerEventTitle={formData.ticketSyncPartnerEventTitle}
							ticketTypes={formData.ticketTypes}
							onEnabledChange={handleTicketSyncEnabledChange}
							onPartnerEventIdChange={handleTicketSyncPartnerEventIdChange}
							onTicketMappingChange={handleTicketSyncMappingChange}
						/>
					) : null}

					{/* イベント公開設定 */}
				<EventSettings
					formData={{
						publicationDateTime: formData.publicationDateTime,
						saleStartDateTime: formData.saleStartDateTime,
						saleEndDateTime: formData.saleEndDateTime,
					}}
					setFormData={setFormData}
					eventDate={formData.date}
				/>

					{/* ボタン */}
					<div className="flex justify-end space-x-3 mt-8 flex-wrap">
						<button
							type="button"
							onClick={() => window.open(`/events/${formData.slug}`, "_blank")}
							className="px-5 py-3 text-sm font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors"
						>
							プレビュー
						</button>
						{formData.status === "draft" && (
							<button
								type="button"
								onClick={(e) => handleSubmit(e, "draft")}
								disabled={isSubmitting}
								className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium shadow-sm hover:shadow-md hover:bg-gray-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
							>
								{isSubmitting ? "保存中..." : "下書き保存"}
							</button>
						)}
						<button
							type="button"
							onClick={(e) => handleSubmit(e, "published")}
							disabled={isSubmitting}
							className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md transform transition-all duration-200 hover:from-violet-600 hover:to-purple-700 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
						>
							{isSubmitting
								? "公開中..."
								: formData.status === "published"
								? "更新する"
								: "公開する"}
						</button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default EditEvent;
